import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, Calculator, Trash2, Edit2, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { CreateCalculationDialog } from "./CreateCalculationDialog";
import { useCalculations } from "@/hooks/useCalculations";

interface CalculationManagerProps {
  templateId: number;
  jotformFormId?: string | null;
  jotformFormIdReferral?: string | null;
}

export function CalculationManager({ templateId, jotformFormId, jotformFormIdReferral }: CalculationManagerProps) {
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingCalculation, setEditingCalculation] = useState<any | null>(null);
  
  const {
    calculations,
    isLoading,
    deleteCalculation,
    isDeleting,
  } = useCalculations(templateId);

  const handleEdit = (calculation: any) => {
    setEditingCalculation(calculation);
    setShowCreateDialog(true);
  };

  const handleDelete = async (calculationId: number) => {
    if (confirm("¿Estás seguro de que deseas eliminar este cálculo?")) {
      await deleteCalculation(calculationId);
    }
  };

  const handleCloseDialog = () => {
    setShowCreateDialog(false);
    setEditingCalculation(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-muted-foreground">Cargando cálculos...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Warning when no JotForm is selected */}
      {!jotformFormId && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Esta plantilla no tiene un formulario de JotForm asociado. Por favor, edita la plantilla 
            y selecciona un formulario para poder crear cálculos.
          </AlertDescription>
        </Alert>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Cálculos de Variables</h3>
          <p className="text-sm text-muted-foreground">
            Crea cálculos basados en grupos de respuestas y respuestas individuales
          </p>
        </div>
        <Button
          onClick={() => setShowCreateDialog(true)}
          disabled={!jotformFormId}
        >
          <Plus className="h-4 w-4 mr-2" />
          Nuevo Cálculo
        </Button>
      </div>

      {/* Stats */}
      {calculations && calculations.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Total de Cálculos</CardDescription>
            <CardTitle className="text-3xl">{calculations.length}</CardTitle>
          </CardHeader>
        </Card>
      )}

      {/* Calculations List */}
      {calculations && calculations.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Cálculos Configurados</CardTitle>
            <CardDescription>
              Lista de todos los cálculos creados para esta plantilla
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Operación</TableHead>
                  <TableHead>Fuente de Datos</TableHead>
                  <TableHead>Detalles</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {calculations.map((calc) => (
                  <TableRow key={calc.id}>
                    <TableCell className="font-medium">{calc.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {calc.calculation_type === 'aggregate' ? 'Agregación' :
                         calc.calculation_type === 'formula' ? 'Fórmula' :
                         calc.calculation_type === 'ranking' ? 'Ranking' :
                         'Desconocido'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge>
                        {calc.calculation_type === 'ranking' && calc.config?.operation === 'top' && 'Top (Mayor a Menor)'}
                        {calc.calculation_type === 'ranking' && calc.config?.operation === 'bottom' && 'Bottom (Menor a Mayor)'}
                        {calc.config?.operation === 'count_referrals' && 'Contar Referidos'}
                        {calc.calculation_type !== 'ranking' && calc.config?.operation !== 'count_referrals' && (calc.config?.operation || 'N/A')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {calc.config?.operation === 'count_referrals' ? '-' : (
                        <>
                          {calc.config?.scope === 'main' && 'Formulario Principal'}
                          {calc.config?.scope === 'referral' && 'Formularios de Referidos'}
                          {calc.config?.scope === 'comparison' && 'Comparación (Principal vs Referidos)'}
                          {calc.config?.scope === 'code' && 'Mi Equipo (mismo código)'}
                          {calc.config?.scope === 'launch' && 'Mi Organización (lanzamiento)'}
                          {!calc.config?.scope && 'N/A'}
                        </>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {calc.config?.operation === 'count_referrals' ? (
                        <div className="text-xs text-muted-foreground">
                          Cuenta el número total de referidos
                        </div>
                      ) : calc.calculation_type === 'ranking' ? (
                        <>
                          {calc.config?.limit && (
                            <div>Top {calc.config.limit} preguntas</div>
                          )}
                          {calc.config?.group_name && (
                            <div>Grupo: {calc.config.group_name}</div>
                          )}
                          {calc.config?.format && (
                            <div>Formato: {calc.config.format === 'bullet' ? 'Bulletpoints' : 'Numerado'}</div>
                          )}
                        </>
                      ) : (
                        <>
                          {calc.config?.groupBy && (
                            <div className="font-medium text-primary">
                              Agrupar: {
                                calc.config.groupBy === 'jotform_groups' ? 'Grupos del Formulario' :
                                calc.config.groupBy === 'individual' ? 'Individual' :
                                calc.config.groupBy === 'code' ? 'Equipo' :
                                calc.config.groupBy === 'launch' ? 'Organización' :
                                calc.config.groupBy
                              }
                            </div>
                          )}
                          {calc.config?.group_name && (
                            <div>Grupo: {calc.config.group_name}</div>
                          )}
                          {calc.config?.question_name && (
                            <div>Pregunta: {calc.config.question_name}</div>
                          )}
                          {calc.config?.matrix_config?.row_id && (
                            <div className="text-blue-600 font-medium">
                              Matriz - Fila: {calc.config.matrix_config.row_id}
                            </div>
                          )}
                          {calc.config?.matrix_config?.column_id && (
                            <div className="text-blue-600">
                              Columna: {calc.config.matrix_config.column_id}
                            </div>
                          )}
                        </>
                      )}
                    </TableCell>
                    <TableCell className="text-right space-x-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEdit(calc)}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(calc.id)}
                        disabled={isDeleting}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Calculator className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No hay cálculos configurados</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Crea tu primer cálculo para procesar datos de respuestas
            </p>
            <Button
              onClick={() => setShowCreateDialog(true)}
              disabled={!jotformFormId}
            >
              <Plus className="h-4 w-4 mr-2" />
              Crear Cálculo
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Dialog */}
      <CreateCalculationDialog
        open={showCreateDialog}
        onOpenChange={handleCloseDialog}
        templateId={templateId}
        jotformFormId={jotformFormId}
        jotformFormIdReferral={jotformFormIdReferral}
        calculation={editingCalculation}
      />
    </div>
  );
}
